import { CustomerSearchResult, RentalStats, RevenueSummary } from './models/analytics';
import Service from './Service';

// Owner-scoped reporting (free admin endpoints). Dates are sent as YYYY-MM-DD.
class AnalyticsService {
  private static URL_BASE = process.env.REACT_APP_URL_API;

  // Format as a local YYYY-MM-DD so a date picked in the UI (e.g. Jan 1) isn't
  // shifted to the previous day by a UTC conversion.
  private static toDateParam = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  static getRevenue(
    from: Date,
    to: Date,
    options?: { groupBy?: 'month' | 'week' | 'day' | 'location' | 'station'; locationId?: number; stationId?: number },
  ): Promise<RevenueSummary> {
    const params = new URLSearchParams();
    params.set('from', this.toDateParam(from));
    params.set('to', this.toDateParam(to));
    if (options?.groupBy) params.set('group_by', options.groupBy);
    if (options?.locationId != null) params.set('location_id', String(options.locationId));
    if (options?.stationId != null) params.set('station_id', String(options.stationId));
    return Service.get(`${this.URL_BASE}/admin/analytics/revenue?${params.toString()}`);
  }

  static getRentalStats(
    from: Date,
    to: Date,
    options?: { locationId?: number; stationId?: number },
  ): Promise<RentalStats> {
    const params = new URLSearchParams();
    params.set('from', this.toDateParam(from));
    params.set('to', this.toDateParam(to));
    if (options?.locationId != null) params.set('location_id', String(options.locationId));
    if (options?.stationId != null) params.set('station_id', String(options.stationId));
    return Service.get(`${this.URL_BASE}/admin/analytics/rental-stats?${params.toString()}`);
  }

  // Owner-scoped free-text customer search (name / email / phone) for the
  // manual-rental customer picker.
  static searchCustomers(search: string, limit = 8): Promise<{ customers: CustomerSearchResult[] }> {
    const params = new URLSearchParams();
    params.set('search', search);
    params.set('limit', String(limit));
    params.set('include_pii', 'true');
    return Service.get(`${this.URL_BASE}/admin/customers?${params.toString()}`);
  }
}

export default AnalyticsService;
